/**
 * Media Plan Builder — frontend mirrors of backend/src/types/mediaPlan.ts and
 * the quote/plan envelopes served by backend/src/routes/mediaPlanAdmin.ts.
 * Field names match the server exactly; all money is USD, all CPMs are net
 * unless a field says otherwise.
 */

export type MediaPlanUnitKey = 'ns-15s' | 'ns-30s' | 'ctv-30s' | 'skippable-30s' | 'bumper-6s' | 'sov-takeover';

export const MEDIA_PLAN_UNIT_ORDER: MediaPlanUnitKey[] = ['ns-15s', 'ns-30s', 'ctv-30s', 'skippable-30s', 'bumper-6s', 'sov-takeover'];

export interface MediaPlanUnitDef {
  key: MediaPlanUnitKey;
  label: string;
  shortLabel: string;
  platform: 'multiplatform' | 'ctv';
  platformLabel: string;
  pricing: 'cpm' | 'flat' | 'either';
  benchmarkKey: string | null;
  adFormat: string | null;
  description: string;
}

export type TargetingLayerKey = 'demo' | 'culture-stack' | 'geo' | 'daypart' | 'competitive';
export type GeoScope = 'national' | 'dma' | 'state' | 'zip';

export interface MediaPlanTargeting {
  geo?: { scope: GeoScope; values?: string[] };
  demo?: string[];
  cultureStacks?: string[];
  dayparts?: string[];
  competitiveConquesting?: string[];
}

// --- Rate cards ---------------------------------------------------------------

export type RateCardScope = 'default' | 'category' | 'agency' | 'advertiser';
export type RateCardStatus = 'draft' | 'active' | 'archived';
export type RateCardSourceScope = RateCardScope | 'override';

export interface RateCardUnitPrice {
  cpm?: number;
  flatUsd?: number;
  flatPer?: 'week' | 'flight';
  floorCpm?: number;
  note?: string;
}

export interface RateCardUpcharge {
  layer: TargetingLayerKey;
  label: string;
  cpmAdd: number;
  appliesTo?: MediaPlanUnitKey[];
  freeWhen?: 'age-only';
  note?: string;
}

export interface RateCardAddedValuePolicy {
  bonusPct: number;
  bonusUnits: MediaPlanUnitKey[];
  customItems?: Array<{ minBudgetUsd: number; label: string; valueUsd?: number }>;
  notes?: string;
}

/** How settled a card's prices are; absent = 'final' (see the backend type). */
export type RateCardListStatus = 'final' | 'interim' | 'placeholder';

export interface MediaPlanRateCard {
  id: string;
  name: string;
  scope: RateCardScope;
  categoryKey?: string;
  agencyKey?: string;
  advertiserKey?: string;
  status: RateCardStatus;
  effectiveFrom: string;
  effectiveTo?: string;
  units: Partial<Record<MediaPlanUnitKey, RateCardUnitPrice>>;
  upcharges: RateCardUpcharge[];
  addedValue?: RateCardAddedValuePolicy;
  /** 'all-in': the CPMs already include targeting (a card saved from a plan). Absent = 'base'. */
  pricingMode?: 'base' | 'all-in';
  listStatus?: RateCardListStatus;
  defaultUnitMix?: Partial<Record<MediaPlanUnitKey, number>>;
  minimums?: { budgetUsd?: number };
  notes?: string;
  version: number;
  createdBy: { uid: string; email: string };
  updatedBy: { uid: string; email: string };
  createdAt: string;
  updatedAt: string;
}

/** What POST/PUT /rate-cards accepts (server stamps version/actor/timestamps). */
export type RateCardInput = Omit<MediaPlanRateCard, 'id' | 'version' | 'createdBy' | 'updatedBy' | 'createdAt' | 'updatedAt'> & { id?: string };

export interface RateCardSourceRef {
  cardId: string;
  cardName: string;
  scope: RateCardSourceScope;
  /** The card's version when it priced the plan (plans built since Oct 2026). */
  version?: number;
}

export type UpchargesMode = 'card' | 'none';
export type PricingBasis = 'rate-card' | 'historical-suggestion' | 'manual';

export interface MediaPlanPricingOverrides {
  unitCpm?: Partial<Record<MediaPlanUnitKey, number>>;
  segmentUnitCpm?: Record<string, Partial<Record<MediaPlanUnitKey, number>>>;
  upchargesMode?: UpchargesMode;
  basis?: PricingBasis;
}

export interface RateCardResolution {
  perUnit: Partial<Record<MediaPlanUnitKey, RateCardSourceRef>>;
  upchargesFrom?: RateCardSourceRef;
  addedValueFrom?: RateCardSourceRef;
  matched: Array<RateCardSourceRef & { matchedOn: string }>;
  asOf: string;
  overrides?: MediaPlanPricingOverrides & { addedValuePct?: number };
}

// --- Taxonomy / registry --------------------------------------------------------

export interface MediaPlanCategory {
  key: string;
  label: string;
  description?: string;
  examples?: string[];
}

export interface MediaPlanAgency {
  key: string;
  name: string;
  holdingCompany?: string;
  aliases: string[];
}

export type ResoldVia = 'revolt' | 'ione';

export interface AdvertiserPricingSummary {
  months: number;
  impressions: number;
  revenueUsd: number;
  weightedEcpm: number;
  asOf: string;
  variants?: Array<{ sourceAdvertiserName: string; resoldVia: ResoldVia | null; months: number; impressions: number; revenueUsd: number; weightedEcpm: number }>;
}

export interface AdvertiserRecord {
  key: string;
  name: string;
  normalizedName: string;
  aliases: string[];
  aliasKeys?: string[];
  gamAdvertiserIds: string[];
  categoryKey: string;
  categoryStatus: 'confirmed' | 'suggested';
  categorySource: 'seed' | 'llm' | 'manual';
  categoryRationale?: string;
  parentCompany?: string;
  agencyKey?: string;
  holdingCompany?: string;
  resoldVia?: ResoldVia | null;
  /** Who the advertiser buys for; absent = 'brand'. Only brand history can start a rate. */
  pricingRole?: 'brand' | 'house' | 'resale-partner';
  negotiatedRateCardId?: string;
  pricingSummary?: AdvertiserPricingSummary;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
}

export interface CategorySuggestion {
  categoryKey: string;
  confidence: 'high' | 'medium' | 'low';
  rationale: string;
}

// --- Requests ---------------------------------------------------------------------

export interface MediaPlanSegmentRequest {
  key: string;
  label: string;
  description?: string;
  budgetPct?: number;
  budgetUsd?: number;
  targeting: MediaPlanTargeting;
  unitMix?: Partial<Record<MediaPlanUnitKey, number>>;
}

export interface MediaPlanRequest {
  advertiserName: string;
  advertiserKey?: string;
  agencyKey?: string;
  budgetUsd: number;
  tiersUsd?: number[];
  flightStart: string;
  flightEnd: string;
  objective?: string;
  unitMix?: Partial<Record<MediaPlanUnitKey, number>>;
  targeting: MediaPlanTargeting;
  segments?: MediaPlanSegmentRequest[];
  pricing?: MediaPlanPricingOverrides;
  includeAddedValue: boolean;
  addedValuePctOverride?: number;
  roundImpressionsTo?: 1 | 1000;
  notes?: string;
}

/** Body of POST /plans/quote and POST /plans (create adds `createSheet`, `revisesPlanId`, `requestId`, `quoteFingerprint`, `pricingNotes`). */
export interface PlanInput extends MediaPlanRequest {
  /** An agency typed by hand; a picked `agencyKey` wins over it. */
  agency?: string;
  /** "Direct (no agency)": ignore the advertiser's agency on file too. */
  noAgency?: boolean;
}

// --- Priced output ------------------------------------------------------------------

export interface MediaPlanLineUpcharge {
  layer: TargetingLayerKey;
  label: string;
  cpmAdd: number;
}

export interface MediaPlanLine {
  id: string;
  unitKey: MediaPlanUnitKey;
  kind: 'paid' | 'bonus' | 'custom';
  placementName: string;
  platform: 'multiplatform' | 'ctv';
  flightStart: string;
  flightEnd: string;
  rateType: 'cpm' | 'flat';
  baseCpm: number;
  upcharges: MediaPlanLineUpcharge[];
  netCpm: number;
  flatUsd?: number;
  impressions: number;
  costUsd: number;
  isAddedValue: boolean;
  addedValueValueUsd?: number;
  benchmark?: { ctr: string; vcr: string };
  history?: { advertiserEcpm?: number; categoryEcpm?: number };
  geography: string;
  targetingSummary: string;
  notes?: string;
  segmentKey?: string;
  segmentLabel?: string;
  baseCpmSource?: 'card' | 'override';
  cardCpm?: number;
  /** Where the line's rate came from (plans built since Oct 2026). */
  rateBasis?: RateBasis;
}

export interface MediaPlanTotals {
  paidImpressions: number;
  paidCostUsd: number;
  /** Flat-priced (takeover) cost inside paidCostUsd; absent before Oct 2026. */
  flatCostUsd?: number;
  blendedCpm: number;
  addedValueImpressions: number;
  addedValueValueUsd: number;
  totalImpressions: number;
  remainingBudgetUsd: number;
}

export interface MediaPlanTierSegment {
  key: string;
  label: string;
  description?: string;
  budgetUsd: number;
  budgetPct: number;
  totals: MediaPlanTotals;
  warnings: string[];
}

export interface MediaPlanTier {
  budgetUsd: number;
  label: string;
  lines: MediaPlanLine[];
  totals: MediaPlanTotals;
  warnings: string[];
  segments?: MediaPlanTierSegment[];
}

export type MediaPlanStatus = 'draft' | 'shared' | 'approved' | 'superseded';

export const PLAN_STATUS_LABELS: Record<MediaPlanStatus, string> = {
  draft: 'Draft',
  shared: 'Shared',
  approved: 'Approved',
  superseded: 'Superseded',
};

export interface MediaPlanExport {
  kind: 'google_sheet' | 'xlsx' | 'agency_template' | 'pdf';
  fileId: string;
  /** Sheets: Drive link. PDFs: the RELATIVE authenticated download path. */
  url: string;
  name: string;
  templateId?: string;
  tierIndex?: number;
  createdAt: string;
  /** Who made it; absent on exports made before Oct 2026. A Sheet lives in this person's Drive. */
  createdBy?: { uid: string; email: string };
}

// --- Rate suggestion ------------------------------------------------------------------

export type RateAnchor = 'advertiser-last12' | 'advertiser-all' | 'advertiser-seed' | 'category' | 'portfolio' | 'rate-card';
export type RateConfidence = 'high' | 'medium' | 'low';

export const RATE_ANCHOR_LABELS: Record<RateAnchor, string> = {
  'advertiser-last12': 'Advertiser — last 12 months',
  'advertiser-all': 'Advertiser — all history',
  'advertiser-seed': 'Advertiser — 3-yr analysis totals',
  category: 'Category average',
  portfolio: 'Portfolio average',
  'rate-card': 'Negotiated rate card',
};

export interface UnitRateSuggestion {
  suggestedNetCpm: number | null;
  listCpm?: number;
  flatUsd?: number;
  floorCpm?: number;
  ratio: number;
  source: 'spread' | 'per-format-blend' | 'list' | 'flat';
  flags: string[];
  rationale: string;
}

export interface RateSuggestionEvidence {
  window?: 'last12' | 'all' | 'seed';
  directOnly?: boolean;
  months?: number;
  impressions?: number;
  firstMonth?: string;
  lastMonth?: string;
  advertiserEcpm?: number;
  resoldEcpm?: number;
  categoryEcpm?: number;
  categoryLabel?: string;
  portfolioEcpm: number;
  indexVsCategory?: number;
  indexVsPortfolio?: number;
  shrinkWeight?: number;
  listMixEcpm?: number;
  discountVsList?: number;
  dataThrough?: string;
  monthsSinceData?: number;
  trend?: { last12Ecpm: number; allEcpm: number; pctChange: number };
  contracted?: { count: number; latestMonth: string; minCpm: number; maxCpm: number };
  zeroRateMonths?: number;
}

export interface RateSuggestion {
  anchor: RateAnchor;
  anchorEcpm: number;
  confidence: RateConfidence;
  evidence: RateSuggestionEvidence;
  perUnit: Partial<Record<MediaPlanUnitKey, UnitRateSuggestion>>;
  upchargesMode: UpchargesMode;
  rationale: string;
  flags: string[];
  notes: string[];
}

// --- History ----------------------------------------------------------------------------

export interface PricingMonth {
  month: string;
  impressions: number;
  revenueUsd: number;
  ecpm: number;
  source: 'warehouse' | 'seed-3yr' | 'manual';
  resoldVia: ResoldVia | null;
  sourceAdvertiserName: string;
}

export interface PricingWindowSummary {
  months: number;
  impressions: number;
  revenueUsd: number;
  weightedEcpm: number;
  firstMonth?: string;
  lastMonth?: string;
}

export interface AdvertiserPricingHistory {
  advertiser: { key: string; name: string; categoryKey: string };
  months: PricingMonth[];
  zeroRateMonths: PricingMonth[];
  all: PricingWindowSummary;
  last12: PricingWindowSummary;
  direct: PricingWindowSummary;
  resold: PricingWindowSummary;
  summaryOnly?: { weightedEcpm: number; months: number; impressions: number; revenueUsd: number; asOf: string };
  contracted: Array<{ month: string; label: string; cpm?: number; flatRate?: number; agency?: string }>;
  category?: { key: string; label: string; weightedEcpm: number; advertisers: number; months?: number; impressions?: number; revenueUsd?: number; indexVsPortfolio: number };
  portfolioEcpm: number;
  dataThrough?: string;
  sources: string[];
}

// --- Pricing policy (Oct 2026) ------------------------------------------------------------
// Mirrors backend/src/types/mediaPlan.ts. Plans built before the policy carry `suggestion`
// instead, so every field the plan page reads from here is optional on the doc.

/** 'partial': one history source failed; 'unavailable': none could be read, so rates started at list. */
export type HistoryStatus = 'ok' | 'partial' | 'unavailable';

/** Where a line's rate came from, in pre-fill order. */
export type RateBasis = 'seller' | 'negotiated' | 'history' | 'list';

export type HistoryReferenceLevel = 'window' | 'last-active' | 'own-mix';
export type HistoryReferenceFlag = 'ctv-inferred' | 'mix-unknown';

export interface HistoryReferenceChecks {
  months: boolean;
  impressions: boolean;
  /** null when the unit has no list price. */
  listRatio: boolean | null;
  brand: boolean;
  inWindow: boolean;
}

export interface UnitHistoryReference {
  level: HistoryReferenceLevel;
  ecpm: number;
  months: number;
  paidImpressions: number;
  revenueUsd: number;
  firstMonth: string;
  lastMonth: string;
  monthsBeforeFlight: number;
  /** "Nov 2025 – Sep 2026", or "Oct 2024 – Sep 2025 · 14 months before flight". */
  windowLabel: string;
  listIndex?: number;
  flags: HistoryReferenceFlag[];
  checks: HistoryReferenceChecks;
  solid: boolean;
  applyable: boolean;
}

export interface EffectiveUnitPricing {
  baseCpm: number;
  /** true: targeting is included in baseCpm, so no upcharges are itemized. */
  foldUpcharges: boolean;
  basis: RateBasis;
}

/** One audience segment × ad unit: list, history, starting rate and the rate the plan uses. */
export interface PricingRateRow {
  /** 'all' for a single-audience plan. */
  segmentKey: string;
  unitKey: MediaPlanUnitKey;
  rateType: 'cpm' | 'flat';
  list: (RateCardUnitPrice & { source: RateCardSourceRef }) | null;
  upcharges: MediaPlanLineUpcharge[];
  listNetCpm?: number;
  history: UnitHistoryReference | null;
  prefill: { cpm: number; basis: Exclude<RateBasis, 'seller'> } | null;
  effective: (EffectiveUnitPricing & { netCpm: number }) | null;
  /** effective ÷ list − 1; negative = below list. */
  vsList?: number;
  /** effective ÷ history − 1; negative = below what they paid. */
  vsHistory?: number;
}

export type GuardrailKind = 'below-list' | 'below-history' | 'above-list';

/** A rate the seller had to explain (internally) before the plan was created. */
export interface GuardrailFinding {
  /** `${kind}:${segmentKey}:${unitKey}` — the key its reason is stored under. */
  id: string;
  kind: GuardrailKind;
  segmentKey: string;
  unitKey: MediaPlanUnitKey;
  netCpm: number;
  comparedCpm: number;
  ratio: number;
  requiresReason: true;
  message: string;
}

export interface MediaPlanPricingReview {
  findings: GuardrailFinding[];
  /** Finding id → the seller's reason. Internal: never on a client file. */
  reasons: Record<string, string>;
  by: { uid: string; email: string };
  at: string;
}

export interface MediaPlanStatusChange {
  from: MediaPlanStatus;
  to: MediaPlanStatus;
  by: { uid: string; email: string };
  at: string;
  /** Set when another plan caused it (approving a revision supersedes the approved prior). */
  viaPlanId?: string;
}

// --- Plan document + quote envelopes ----------------------------------------------------

export interface MediaPlanDoc {
  id: string;
  version: number;
  supersedesPlanId?: string;
  /** The first version's id, shared by every version (plans built since Oct 2026). */
  chainId?: string;
  /** The newer version made from this one (plans built since Oct 2026). */
  supersededByPlanId?: string;
  status: MediaPlanStatus;
  advertiser: { key: string; name: string; categoryKey: string; categoryLabel: string; categoryStatus: 'confirmed' | 'suggested' };
  agencyKey?: string;
  /** The agency as typed or matched (stored since Oct 2026; older plans get it from GET /plans/:id). */
  agencyLabel?: string;
  request: MediaPlanRequest;
  rateCard: {
    resolution: RateCardResolution;
    snapshot: {
      units: Partial<Record<MediaPlanUnitKey, RateCardUnitPrice>>;
      upcharges: RateCardUpcharge[];
      addedValue: RateCardAddedValuePolicy | null;
      cardUnits?: Partial<Record<MediaPlanUnitKey, RateCardUnitPrice>>;
    };
  };
  /** The pre-Oct 2026 rate suggestion; newer plans carry `rates`. */
  suggestion?: RateSuggestion;
  policyVersion?: string;
  rates?: PricingRateRow[];
  historyStatus?: HistoryStatus;
  quoteFingerprint?: string;
  requestId?: string;
  pricingReview?: MediaPlanPricingReview;
  /** YYYY-MM-DD the proposal's rates hold until, frozen at create. */
  validThrough?: string;
  tiers: MediaPlanTier[];
  warnings: string[];
  exports: MediaPlanExport[];
  /** `name` is the seller's display name (plans built since Oct 2026). */
  createdBy: { uid: string; email: string; name?: string };
  source: 'chat' | 'mcp' | 'ui' | 'script';
  sessionId?: string;
  statusUpdatedAt?: string;
  statusUpdatedBy?: { uid: string; email: string };
  /** Last 50 status changes, oldest first. */
  statusHistory?: MediaPlanStatusChange[];
  updatedBy?: { uid: string; email: string };
  createdAt: string;
  updatedAt: string;
}

export interface BenchmarkRow {
  unitKey: MediaPlanUnitKey;
  label: string;
  ctr?: string;
  ctrRange?: string;
  vcr?: string;
  vcrRange?: string;
  note?: string;
}

export interface MediaPlanQuote {
  /** 2 since the Oct 2026 pricing policy, which sends everything from `policyVersion` to `notes`. */
  quoteVersion?: 2;
  asOf: string;
  /** The normalized request the plan will be built from; `pricing` holds only the seller's own rates. */
  request: MediaPlanRequest;
  advertiser: MediaPlanDoc['advertiser'] & { matchedBy: string; resoldVia?: ResoldVia | null; negotiatedRateCardId?: string; pricingRole?: AdvertiserRecord['pricingRole'] };
  agencyLabel?: string;
  rateCard: MediaPlanDoc['rateCard'] & { cardNames: string[]; placeholder: boolean; minimumBudgetUsd?: number; defaultUnitMix?: Partial<Record<MediaPlanUnitKey, number>> };
  policyVersion?: string;
  historyStatus?: HistoryStatus;
  /** One row per audience × ad unit: list, what they paid, the starting rate and the rate used. */
  rates?: PricingRateRow[];
  /** Rates that need an internal reason before the plan can be created. */
  guardrails?: GuardrailFinding[];
  /** Sent back with create: a different price at create answers 409 `quote_changed`. */
  fingerprint?: string;
  /** Plain-language notes on the pricing (history unavailable, house account…). */
  notes?: string[];
  /** @deprecated The pre-Oct 2026 suggestion; it never prices a plan. */
  suggestion?: RateSuggestion;
  history: AdvertiserPricingHistory | null;
  benchmarks: BenchmarkRow[];
  preview: { tiers: MediaPlanTier[]; warnings: string[] };
}

/** A name nobody has added to the registry yet, as a quote reports it (no lookups). */
export interface NeedsAdvertiserStub {
  name: string;
  canonicalName: string;
  proposedKey: string;
  categories: Array<{ key: string; label: string }>;
}

/** The new-advertiser draft from POST /advertisers/resolve: a category guess and matching GAM companies. */
export interface NeedsAdvertiser extends NeedsAdvertiserStub {
  suggestion: CategorySuggestion;
  gamCandidates: Array<{ id: string; name: string }>;
}

/** A brief priced in Culture Genius chat, opened in the builder (?draft=<id>); it expires after 14 days. */
export interface MediaPlanDraftDoc {
  id: string;
  createdBy: { uid: string; email: string };
  source: MediaPlanDoc['source'];
  request: MediaPlanRequest;
  /** The agency as typed in chat. */
  agencyName?: string;
  /** Open as the next version of this plan. */
  revisesPlanId?: string;
  /** What chat quoted; absent when the advertiser isn't in the registry yet. */
  quoted?: {
    fingerprint: string;
    policyVersion: string;
    tiers: Array<{ label: string; budgetUsd: number; paidCostUsd: number; paidImpressions: number; blendedCpm: number }>;
    guardrailIds: string[];
  };
  createdAt: string;
  expiresAt: string;
}

export interface MediaPlanReference {
  units: MediaPlanUnitDef[];
  layers: TargetingLayerKey[];
  categories: { categories: MediaPlanCategory[] };
  agencies: { agencies: MediaPlanAgency[] };
}

export interface SheetExportResult {
  spreadsheetId: string;
  url: string;
  name: string;
}

export interface PdfCardData {
  id: string;
  title: string;
  fileName: string;
  pageCount: number;
  sizeBytes: number;
  version: number;
  createdAt: string;
}

export interface CreatePlanResult {
  plan: MediaPlanDoc;
  sheet: SheetExportResult | null;
  sheetError: { code: 'google_reconnect_required' | 'client_file_leak' | 'sheet_export_failed'; message: string } | null;
  history: AdvertiserPricingHistory | null;
  agencyLabel?: string;
}
